import {Component, effect, inject, input, signal} from '@angular/core';
import moment from 'moment';
import {NgxEchartsDirective, provideEchartsCore} from 'ngx-echarts';
import {EChartsCoreOption} from 'echarts';
import * as echarts from 'echarts/core';
import {BarChart, LineChart} from 'echarts/charts';
import {LabelLayout, UniversalTransition} from 'echarts/features';
import {
  DatasetComponent,
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  MarkPointComponent,
  TitleComponent,
  ToolboxComponent,
  TooltipComponent
} from 'echarts/components';
import {CanvasRenderer} from 'echarts/renderers';
import {AnalyticsReportResponse, AnalyticsService} from '../../../../data/reports-analytics/analytics.service';
import {MatCard, MatCardContent} from '@angular/material/card';

echarts.use([
  // renderer
  CanvasRenderer,
  // charts
  LineChart, BarChart,
  // components
  TooltipComponent, GridComponent, LegendComponent, DatasetComponent,
  DataZoomComponent, TitleComponent, ToolboxComponent,
  MarkPointComponent, MarkLineComponent,
  // features
  LabelLayout, UniversalTransition]);

@Component({
  selector: 'app-analytics-report-view',
  templateUrl: './analytics-report-view.component.html',
  styleUrls: ['./analytics-report-view.component.scss'],
  imports: [
    NgxEchartsDirective,
    MatCard,
    MatCardContent
  ],
  providers: [provideEchartsCore({echarts})],
  standalone: true
})
export class AnalyticsReportViewComponent {

  private analyticsService = inject(AnalyticsService);

  dateFrom = input<moment.Moment>(null);
  dateTo = input<moment.Moment>(null);

  data: AnalyticsReportResponse;

  isLoading = signal(true);

  loadingOptions = {
    text: 'Loading data...',
    color: '#3a83ff',
    textColor: '#000',
    maskColor: 'rgba(255, 255, 255, 0.8)',
    zlevel: 0
  };

  chartOption: EChartsCoreOption = {
    legend: {
      show: true
    },
    dataZoom: [
      {type: 'inside'}, // Allows zooming with mouse wheel
      {type: 'slider'}  // Adds the visual scrollbar at the bottom
    ],
    tooltip: {
      show: true,
      trigger: 'axis',
      axisPointer: {
        type: 'line'        // Shows the vertical line like in GA
      }
    },
    xAxis: {
      type: 'time',
    },
    yAxis: {
      type: 'value',
      name: 'Active users'
    },
    series: [
      {
        data: [],
        type: 'line',
      },
    ],
  };


  constructor() {

    effect(() => {
      const dateFrom = this.dateFrom();
      const dateTo = this.dateTo() || moment();
      if (dateFrom && dateTo && dateFrom.isBefore(dateTo)) {
        this.loadData(dateFrom, dateTo);
      }
    });

  }

  loadData(dateFrom, dateTo) {
    this.isLoading.set(true);
    this.analyticsService.getAnalyticsReport(
      'activeUsers', dateFrom.format('YYYY-MM-DD'), dateTo.format('YYYY-MM-DD')
    ).then(response => {
      this.data = response.data;
      this.chartOption = {
        ...this.chartOption,
        series: [
          {
            data: this.data.stats.map(item => [item.time, item.activeUsers]),
            type: 'line',
            symbolSize: (value: any) => { // don't show points for 0 values
              return value[1] === 0 ? 0 : 4;
            },
            smooth: true, // This gives it that premium GA look
            areaStyle: {
              opacity: 0.1 // Adds the subtle color fill under the line
            },
            markLine: {
              lineStyle: {
                type: 'dashed',
                color: '#999'
              },
              label: {
                show: true,
                position: 'end',           // Put it at the end of the line
                formatter: '{b}: {c}'      // {b} = name ('Avg'), {c} = calculated value
              },
              data: [{type: 'average', name: 'Avg'}] // Automatically calculates the average line
            }
          },
        ]
      };

    }).catch(error => {
      console.error(error);
    }).finally(() => {
      this.isLoading.set(false);
    });

  }

}
